<template>
  <div>
    <!-- Breadcrumb -->
    <nav v-if="breadcrumb" class="border-b border-gray-300 py-2 px-2 mb-4 md:px-0">
      <div class="flex justify-between items-center">
        <div>
          <div class="hidden md:flex text-sm text-gray-500">
            <router-link :to="{ name: 'home' }" class="hover:text-primary">Home</router-link>
            <span class="mx-2">/</span>
            <span class="hover:text-primary">M-Pesa Transactions</span>
          </div>
          <div class="text-2xl text-primary font-bold mt-2">M-Pesa Transactions</div>
        </div>
      </div>
    </nav>

    <!-- Table container -->
    <div class="bg-white border rounded-lg shadow p-4">
      <div class="flex justify-between items-center mb-4">
        <div class="font-semibold text-gray-700 text-lg">Transactions</div>
        <button
          @click="exportToCSV"
          class="bg-primary text-white px-4 py-2 rounded-lg shadow hover:bg-primary-dark transition"
        >
          Export CSV
        </button>
      </div>

      <!-- Table -->
      <div class="overflow-x-auto rounded-lg shadow">
        <table v-if="!loading" class="table-auto w-full border-collapse">
          <thead>
            <tr class="bg-gray-100 text-gray-700 text-sm uppercase tracking-wide">
              <th class="px-4 py-3 text-left">#</th>
              <th class="px-4 py-3 text-left">Transaction ID</th>
              <th class="px-4 py-3 text-left">Phone</th>
              <th class="px-4 py-3 text-left">Amount</th>
              <th class="px-4 py-3 text-left">M-Pesa Shortcode</th>
              <th class="px-4 py-3 text-center">Status</th>
              <th class="px-4 py-3 text-left">Date</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(txn, index) in transactions"
              :key="txn.id"
              class="border-t hover:bg-gray-50 transition-colors"
            >
              <td class="px-4 py-3">{{ index + 1 + (currentPage - 1) * perPage }}</td>
              <td class="px-4 py-3 font-mono text-sm">{{ txn.transaction_id }}</td>
              <td class="px-4 py-3">{{ txn.phone_number }}</td>
              <td class="px-4 py-3">{{ txn.amount }}</td>
              <td class="px-4 py-3">{{ txn.short_code }}</td>
              <td class="px-4 py-3 text-center">
                <span
                  class="px-2 py-1 rounded text-xs font-medium"
                  :class="txn.status == 1 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'"
                >
                  {{ txn.status == 1 ? 'Completed' : 'Incomplete' }}
                </span>
              </td>
              <td class="px-4 py-3 text-gray-700">{{ formatDate(txn.created_at) }}</td>
            </tr>
          </tbody>
        </table>

        <div v-else class="py-6 text-center w-full text-gray-600">
          <feather animation="spin" type="loader" size="32" class="mr-2"></feather>
          <p>Loading transactions...</p>
        </div>
      </div>

      <!-- No results -->
      <div v-if="!transactions.length && !loading" class="mt-4 text-gray-500 text-sm text-center">
        No M-Pesa transactions found.
      </div>

      <!-- Pagination -->
      <div
        v-if="lastPage > 1"
        class="flex justify-between items-center mt-6 text-sm text-gray-700 select-none"
      >
        <div>
          Page {{ currentPage }} of {{ lastPage }}
        </div>

        <div class="flex items-center space-x-2">
          <button
            @click="goToPage(currentPage - 1)"
            :disabled="currentPage === 1 || loading"
            class="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
          >
            Prev
          </button>

          <button
            v-for="page in visiblePages"
            :key="page"
            @click="goToPage(page)"
            :disabled="page === currentPage || loading"
            :class="[
              'px-3 py-1 rounded',
              page === currentPage ? 'bg-primary text-white' : 'bg-gray-200 hover:bg-gray-300',
            ]"
          >
            {{ page }}
          </button>

          <button
            @click="goToPage(currentPage + 1)"
            :disabled="currentPage === lastPage || loading"
            class="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "MpesaTransactions",
  data() {
    const today = new Date().toISOString().split("T")[0];
    return {
      transactions: [],
      organizationId: this.$store.getters.orgId,
      startDate: "1970-01-01",
      endDate: today,
      loading: false,
      error: null,
      currentPage: 1,
      lastPage: 1,
      perPage: 50,
      totalCount: 0,
    };
  },
  computed: {
    visiblePages() {
      const range = [];
      const start = Math.max(1, this.currentPage - 2);
      const end = Math.min(this.lastPage, this.currentPage + 2);
      for (let i = start; i <= end; i++) {
        range.push(i);
      }
      return range;
    },
  },
  mounted() {
  //  this.fetchTransactions();
  },
  methods: {
    async fetchTransactions(page = 1) {
      this.loading = true;
      this.error = null;
      try {
        const response = await axios.post(
          "https://apiresources.brandfi.co.ke/api/v1/getmpesatransactions",
          {
            organization_id: this.organizationId,
            start_date: this.startDate,
            end_date: this.endDate,
            page,
          } 
        );

        const data = response.data.data;
        this.transactions = Array.isArray(data.data) ? data.data : [];
        this.currentPage = data.current_page || page;
        this.lastPage = data.last_page || 1;
        this.perPage = data.per_page || 50;
        this.totalCount = data.count || 0;
      } catch (err) {
        this.error = "Failed to fetch transactions.";
      } finally {
        this.loading = false;
      }
    },
    goToPage(page) {
      if (page < 1 || page > this.lastPage || this.loading) return;
      this.currentPage = page; // update immediately so button feels responsive
      this.fetchTransactions(page);
    },
    formatDate(dateString) {
      if (!dateString) return "—";
      const date = new Date(dateString);
      return date.toLocaleString();
    },
    exportToCSV() {
      const csv = [
        ["Transaction ID", "Phone", "Amount", "Status", "Date"],
        ...this.transactions.map((txn) => [
          txn.transaction_id,
          txn.phone_number,
          txn.amount,
          txn.status == 1 ? "Completed" : "Incomplete",
          this.formatDate(txn.created_at),
        ]),
      ]
        .map((row) => row.join(","))
        .join("\n");

      const blob = new Blob([csv], { type: "text/csv" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "mpesa_transactions.csv";
      link.click();
    },
  },
};
</script>

<style scoped>
.disabled\:opacity-50 {
  opacity: 0.5;
  pointer-events: none;
}
</style>
